import React, { useEffect, useState } from 'react';
import { getFiles, uploadFile, downloadFile } from '../utils/crmApi';
import { useToast } from './Toast';

const formatSize = (size) => {
    if (!size && size !== 0) return '-';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const FilesPanel = ({ entityType, entityId, ownerName }) => {
    const { showSuccess, showError } = useToast();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const fetchFiles = async () => {
        if (!entityType || !entityId) return;
        setLoading(true);
        setError('');
        try {
            const result = await getFiles(entityType, entityId);
            setFiles(result.data || []);
        } catch (err) {
            setError(err.message || 'Failed to load files');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, [entityType, entityId]);

    const handleUpload = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                setUploading(true);
                const result = reader.result || '';
                const base64Data = String(result).split(',')[1] || '';
                await uploadFile({
                    entityType,
                    entityId,
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    base64Data,
                });
                showSuccess('File uploaded');
                await fetchFiles();
            } catch (err) {
                console.error('Upload error:', err);
                showError(err.message || 'Failed to upload file');
            } finally {
                setUploading(false);
            }
        };
        reader.onerror = () => {
            showError('Failed to read file');
        };
        reader.readAsDataURL(file);
    };

    const handleDownload = async (file) => {
        try {
            const blob = await downloadFile(file.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.fileName || 'file';
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            showError(err.message || 'Failed to download file');
        }
    };

    return (
        <div className="crm-files-panel">
            <div className="crm-files-header">
                <h3>Files</h3>
                <label className="crm-btn crm-btn-secondary crm-btn-sm">
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input
                        type="file"
                        style={{ display: 'none' }}
                        onChange={(event) => handleUpload(event.target.files?.[0])}
                        disabled={uploading}
                    />
                </label>
            </div>

            {loading ? (
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            ) : error ? (
                <div className="crm-empty-state">
                    <p>{error}</p>
                    <button className="crm-btn crm-btn-primary" onClick={fetchFiles}>Retry</button>
                </div>
            ) : files.length === 0 ? (
                <div className="crm-empty-state">
                    <h4>No files yet</h4>
                    <p>Upload documents, brochures, or agreements here.</p>
                </div>
            ) : (
                <div className="crm-files-list">
                    {files.map((file) => (
                        <div key={file.id} className="crm-file-row">
                            <div className="crm-file-meta">
                                <div className="crm-file-name">{file.fileName}</div>
                                <div className="crm-file-sub">
                                    <span>{formatSize(file.size)}</span>
                                    <span>•</span>
                                    <span>{ownerName || file.uploadedByName || 'Unknown'}</span>
                                    <span>•</span>
                                    <span>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : '-'}</span>
                                </div>
                            </div>
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                onClick={() => handleDownload(file)}
                            >
                                Download
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FilesPanel;


